import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "@api";
import imageCompression from "browser-image-compression";
import "./LeagueTeamsAuth.css";

function LeagueTeamsAuth({
  seasonId,
  leagueId,
  teams,
  setTeams,
  teamGameCounts,
  showTeamHideControls,
  allPlayers,
  setAllPlayers,
  setPreviewImage,
  setMessage,
}) {
  const [isAddTeamsModalOpen, setIsAddTeamsModalOpen] = useState(false);
  const [addTeamStep, setAddTeamStep] = useState("names");
  const [bulkTeamRows, setBulkTeamRows] = useState([
    { name: "" },
    { name: "" },
    { name: "" },
    { name: "" },
    { name: "" },
  ]);
  const [teamReviewList, setTeamReviewList] = useState([]);
  const [isCheckingTeams, setIsCheckingTeams] = useState(false);
  const [isSavingTeams, setIsSavingTeams] = useState(false);
  const [addTeamError, setAddTeamError] = useState("");

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [captainSearch, setCaptainSearch] = useState("");
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [updateForm, setUpdateForm] = useState({
    id: null,
    name: "",
    captain: "",
    captainId: "",
  });

  const fetchTeamsInLeague = useCallback(async () => {
    if (!seasonId || !leagueId) return;

    try {
      const res = await api.get(
        `/api/seasonsv2/${seasonId}/leagues/${leagueId}/teams`,
        { withCredentials: true }
      );

      setTeams(res.data.teams || []);
    } catch (err) {
      console.error("Error fetching teams in league:", err);
      setMessage("Failed to fetch teams");
    }
  }, [seasonId, leagueId, setTeams, setMessage]);

  useEffect(() => {
    fetchTeamsInLeague();
  }, [fetchTeamsInLeague]);

  const sortedTeams = useMemo(() => {
    return [...teams].sort((a, b) => {
      const rankA = Number(a.rank || 9999);
      const rankB = Number(b.rank || 9999);

      if (rankA !== rankB) return rankA - rankB;

      return (a.name || "").localeCompare(b.name || "");
    });
  }, [teams]);

  const normalizeTeamName = (name) =>
    (name || "").toLowerCase().replace(/\s+/g, " ").trim();

  const getEnteredTeamNames = () => {
    return bulkTeamRows
      .map((row) => row.name.replace(/\s+/g, " ").trim())
      .filter(Boolean);
  };

  const getDuplicateEnteredNames = (names) => {
    const seen = new Set();
    const duplicates = new Set();

    names.forEach((name) => {
      const key = normalizeTeamName(name);

      if (seen.has(key)) {
        duplicates.add(name);
      }

      seen.add(key);
    });

    return [...duplicates];
  };

  const getAlreadyInLeagueNames = (names) => {
    const existingTeamNames = teams.map((team) =>
      normalizeTeamName(team.name || "")
    );

    return names.filter((name) =>
      existingTeamNames.includes(normalizeTeamName(name))
    );
  };

  const resetAddTeamsModal = () => {
    setBulkTeamRows([
      { name: "" },
      { name: "" },
      { name: "" },
      { name: "" },
      { name: "" },
    ]);
    setTeamReviewList([]);
    setAddTeamStep("names");
    setAddTeamError("");
    setIsCheckingTeams(false);
    setIsSavingTeams(false);
  };

  const handleOpenAddTeamsModal = () => {
    resetAddTeamsModal();
    setIsAddTeamsModalOpen(true);
  };

  const handleBulkTeamRowChange = (index, value) => {
    setBulkTeamRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, name: value } : row
      )
    );
  };

  const handleAddBulkTeamRow = () => {
    setBulkTeamRows((prev) => [...prev, { name: "" }]);
  };

  const handleRemoveBulkTeamRow = (index) => {
    setBulkTeamRows((prev) =>
      prev.length === 1
        ? [{ name: "" }]
        : prev.filter((_, rowIndex) => rowIndex !== index)
    );
  };

  const updateTeamReviewChoice = (tempId, updates) => {
    setTeamReviewList((prev) =>
      prev.map((item) =>
        item.tempId === tempId ? { ...item, ...updates } : item
      )
    );
  };

  const getSelectedTeamMatch = (item) => {
    return item.matches.find(
      (match) => Number(match.id) === Number(item.selectedTeamId)
    );
  };

  const getTeamHistoryLines = (match) => {
    if (Array.isArray(match.history) && match.history.length > 0) {
      return match.history.map((historyItem) => {
        const leagueName = historyItem.leagueName || "Unknown League";
        const seasonName = historyItem.seasonName || "Unknown Season";
        const captain = historyItem.captain
          ? `Captain: ${historyItem.captain}`
          : "No captain";
        const rosterCount = Number(historyItem.rosterCount || 0);

        return `${leagueName} — ${seasonName} — ${captain} — ${rosterCount} player${rosterCount === 1 ? "" : "s"
          }`;
      });
    }

    const fallbackCount = Number(match.playersCount || 0);
    const fallbackCaptain = match.captain
      ? `Captain: ${match.captain}`
      : "No captain";

    return [
      `${fallbackCaptain} — Previous roster: ${fallbackCount} player${fallbackCount === 1 ? "" : "s"
      }`,
    ];
  };

  const handleCheckTeams = async () => {
    if (!seasonId || !leagueId) return;

    const rawNames = getEnteredTeamNames();

    if (!rawNames.length) {
      setAddTeamError("Please enter at least one team name.");
      return;
    }

    const duplicateNames = getDuplicateEnteredNames(rawNames);

    if (duplicateNames.length > 0) {
      setAddTeamError(
        `Duplicate team name in list: ${duplicateNames.join(", ")}`
      );
      return;
    }

    const alreadyInLeague = getAlreadyInLeagueNames(rawNames);

    if (alreadyInLeague.length > 0) {
      setAddTeamError(
        `Already in this league: ${alreadyInLeague.join(", ")}`
      );
      return;
    }

    try {
      setIsCheckingTeams(true);
      setAddTeamError("");

      const matchRes = await api.post(
        `/api/seasonsv2/${seasonId}/leagues/${leagueId}/teams/check-matches`,
        { names: rawNames },
        { withCredentials: true }
      );

      const apiMatches = matchRes.data.matches || [];

      const reviewList = rawNames.map((enteredName, index) => {
        const matchItem =
          apiMatches.find(
            (item) =>
              normalizeTeamName(item.enteredName) ===
              normalizeTeamName(enteredName)
          ) || {};

        const matches = matchItem.matches || [];

        return {
          tempId: `${enteredName}-${index}`,
          enteredName,
          matches,
          action: matches.length > 0 ? "existing" : "new",
          selectedTeamId: matches[0]?.id || "",
          copyRoster: "",
        };
      });

      setTeamReviewList(reviewList);
      setAddTeamStep("review");
    } catch (err) {
      console.error("Error checking teams:", err.response?.data || err.message);
      setAddTeamError(
        err.response?.data?.message || "Failed to check teams."
      );
    } finally {
      setIsCheckingTeams(false);
    }
  };

  const handleAddTeamsToLeague = async () => {
    if (!teamReviewList.length) {
      setAddTeamError("Please check teams first.");
      return;
    }

    for (const item of teamReviewList) {
      if (item.action === "existing" && !item.selectedTeamId) {
        setAddTeamError(
          `Please choose an existing team for "${item.enteredName}".`
        );
        return;
      }

      if (item.action === "existing" && item.copyRoster === "") {
        setAddTeamError(
          `Please choose Copy Previous Roster or Start Empty for "${item.enteredName}".`
        );
        return;
      }
    }

    try {
      setIsSavingTeams(true);
      setAddTeamError("");

      let addedCount = 0;
      const failedTeams = [];

      for (const item of teamReviewList) {
        try {
          const formData = new FormData();

          if (item.action === "existing" && item.selectedTeamId) {
            const selectedMatch = getSelectedTeamMatch(item);

            formData.append("copyFromTeamId", item.selectedTeamId);
            formData.append("copyRoster", item.copyRoster);

            if (item.copyRoster === "true") {
              formData.append("captain_id", selectedMatch?.captain_id || "");
              formData.append("captain", selectedMatch?.captain || "");
            }
          } else {
            formData.append("name", item.enteredName);
          }

          await api.post(
            `/api/seasonsv2/${seasonId}/leagues/${leagueId}/teams`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              withCredentials: true,
            }
          );

          addedCount += 1;
        } catch (err) {
          failedTeams.push(item.enteredName);
          console.error(
            "Error adding team:",
            item.enteredName,
            err.response?.data || err.message
          );
        }
      }

      await fetchTeamsInLeague();

      setIsSavingTeams(false);
      setIsAddTeamsModalOpen(false);
      resetAddTeamsModal();

      if (failedTeams.length > 0) {
        setMessage(
          `Added ${addedCount} team${addedCount === 1 ? "" : "s"}. ${failedTeams.length
          } failed.`
        );
      } else {
        setMessage(
          `Added ${addedCount} team${addedCount === 1 ? "" : "s"} to league.`
        );
      }

      setTimeout(() => setMessage(""), 4000);
    } catch (err) {
      console.error("Error adding teams:", err.response?.data || err.message);
      setAddTeamError(
        err.response?.data?.message || "Failed to add teams to league."
      );
      setIsSavingTeams(false);
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();

      formData.append("name", updateForm.name);
      formData.append("captainId", updateForm.captainId || "");
      formData.append("captain", updateForm.captain || "");

      if (updateForm.logo) {
        formData.append("logo", updateForm.logo);
      }

      if (updateForm.season_team_logo) {
        formData.append("season_team_logo", updateForm.season_team_logo);
      }

      if (updateForm.season_team_photo) {
        formData.append("season_team_photo", updateForm.season_team_photo);
      }

      const res = await api.put(
        `/api/seasonsv2/${seasonId}/leagues/${leagueId}/teams/${updateForm.id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );

      setTeams((prev) =>
        prev.map((team) =>
          team.id === updateForm.id ? { ...team, ...res.data.team } : team
        )
      );

      setMessage("Team updated!");
      setIsUpdateModalOpen(false);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating team:", err);
      setMessage("Failed to update team");
    }
  };

  const openUpdateModal = async (team) => {
    try {
      const res = await api.get(
        `/api/seasonsv2/${seasonId}/leagues/${leagueId}/teams/${team.id}/players`,
        {
          withCredentials: true,
        }
      );

      setAllPlayers(res.data.players || []);
    } catch (err) {
      console.error("Error fetching players for team:", err);
    }

    setCaptainSearch("");

    setUpdateForm({
      id: team.id,
      name: team.name || "",
      captain: team.captain || "",
      captainId: team.captain_id || "",

      logo: null,
      season_team_logo: null,
      season_team_photo: null,

      existingLogo: team.logo || "",
      existingSeasonLogo: team.season_team_logo || "",
      existingSeasonTeamPhoto: team.season_team_photo || "",
    });

    setIsUpdateModalOpen(true);
  };

  const handleDeleteTeams = async () => {
    if (!selectedTeams.length) {
      alert("Please select at least one team.");
      return;
    }

    const selectedTeamsWithGames = teams.filter((team) => {
      if (!selectedTeams.includes(team.id)) return false;

      const lookupId = Number(team.seasons_leagues_teams_id || team.id);
      return (teamGameCounts[lookupId] || 0) > 0;
    });

    if (selectedTeamsWithGames.length > 0) {
      alert("Delete this team's games before deleting the team.");
      return;
    }

    if (!window.confirm("Delete selected teams?")) return;

    try {
      await Promise.all(
        selectedTeams.map((id) =>
          api.delete(
            `/api/seasonsv2/${seasonId}/leagues/${leagueId}/teams/${id}`,
            {
              withCredentials: true,
            }
          )
        )
      );

      setTeams((prev) =>
        prev.filter((team) => !selectedTeams.includes(team.id))
      );

      setSelectedTeams([]);
      setMessage("Teams deleted successfully");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error deleting teams:", err);
      setMessage("Failed to delete teams");
    }
  };

  const handleCheckboxChange = (id) => {
    setSelectedTeams((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedTeams.length === teams.length) {
      setSelectedTeams([]);
    } else {
      setSelectedTeams(teams.map((team) => team.id));
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });

      setUpdateForm((prev) => ({
        ...prev,
        logo: compressed,
      }));
    } catch (err) {
      console.error("Error compressing image:", err);
    }
  };

  const handleSeasonLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });

      setUpdateForm((prev) => ({
        ...prev,
        season_team_logo: compressed,
      }));
    } catch (err) {
      console.error("Error compressing season logo:", err);
    }
  };

  const handleSeasonTeamPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      });

      setUpdateForm((prev) => ({
        ...prev,
        season_team_photo: compressed,
      }));
    } catch (err) {
      console.error("Error compressing season team photo:", err);
    }
  };

  const handleToggleHide = async (teamId) => {
    try {
      await api.put(
        `/api/seasonsv2/${seasonId}/leagues/${leagueId}/teams/${teamId}/toggle-hidden`,
        {},
        { withCredentials: true }
      );

      setTeams((prev) =>
        prev.map((team) =>
          team.id === teamId ? { ...team, hidden: !team.hidden } : team
        )
      );
    } catch (err) {
      console.error("Error toggling hide:", err);
    }
  };

  return (
    <div className="teamLeagueTeamsAuthPage-container">
      <div className="teamLeagueTeamsAuthPage-header">
        <div>

          <h2 className="teamLeagueTeamsAuthPage-title">Teams</h2>

          <p className="teamLeagueTeamsAuthPage-subtitle">
            Add teams, manage captains, and update league team records.
          </p>
        </div>

        <div className="teamLeagueTeamsAuthPage-actions">
          <button
            type="button"
            className="teamLeagueTeamsAuthPage-actionBtn teamLeagueTeamsAuthPage-addBtn"
            onClick={handleOpenAddTeamsModal}
          >
            + Add Teams
          </button>

          <button
            type="button"
            className="teamLeagueTeamsAuthPage-actionBtn teamLeagueTeamsAuthPage-deleteBtn"
            onClick={handleDeleteTeams}
            disabled={selectedTeams.length === 0}
            title={
              selectedTeams.length === 0
                ? "Select teams first"
                : "Delete selected teams"
            }
          >
            Delete Selected
          </button>
        </div>
      </div>

      {isAddTeamsModalOpen && (
        <div className="teamLeagueTeamsAuthPage-addTeamsModalOverlay">
          <div className="teamLeagueTeamsAuthPage-addTeamsModal">
            {addTeamStep === "names" && (
              <div>
                <h2 className="teamLeagueTeamsAuthPage-modalTitle">
                  Add Teams to This League
                </h2>

                <p className="teamLeagueTeamsAuthPage-modalSubtitle">
                  Enter team names below. Rosters, captains, logos, and photos can be handled after.
                </p>

                {addTeamError && (
                  <div className="teamLeagueTeamsAuthPage-alert">
                    {addTeamError}
                  </div>
                )}

                <div className="teamLeagueTeamsAuthPage-teamRows">
                  {bulkTeamRows.map((row, index) => (
                    <div
                      key={index}
                      className="teamLeagueTeamsAuthPage-teamRow"
                    >
                      <input
                        className="teamLeagueTeamsAuthPage-modalInput"
                        type="text"
                        placeholder="Team Name"
                        value={row.name}
                        onChange={(e) =>
                          handleBulkTeamRowChange(index, e.target.value)
                        }
                      />

                      <button
                        type="button"
                        className="teamLeagueTeamsAuthPage-removeRowBtn"
                        onClick={() => handleRemoveBulkTeamRow(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="teamLeagueTeamsAuthPage-addRowBtn"
                  onClick={handleAddBulkTeamRow}
                >
                  + Add Another Team
                </button>


                <div className="teamLeagueTeamsAuthPage-modalActions">
                  <button
                    type="button"
                    className="teamLeagueTeamsAuthPage-modalCancelBtn"
                    onClick={() => {
                      setIsAddTeamsModalOpen(false);
                      resetAddTeamsModal();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="teamLeagueTeamsAuthPage-modalSubmitBtn"
                    disabled={isCheckingTeams}
                    onClick={handleCheckTeams}
                  >
                    {isCheckingTeams ? "Checking..." : "Check Teams"}
                  </button>


                </div>
              </div>
            )}

            {addTeamStep === "review" && (
              <div>
                <h2 className="teamLeagueTeamsAuthPage-modalTitle">
                  Review Teams
                </h2>

                <p className="teamLeagueTeamsAuthPage-modalSubtitle">
                  Choose whether to use an existing team record or create a new one.
                </p>

                {addTeamError && (
                  <div className="teamLeagueTeamsAuthPage-alert">
                    {addTeamError}
                  </div>
                )}

                <div className="teamLeagueTeamsAuthPage-reviewList">
                  {teamReviewList.map((item) => (
                    <div
                      key={item.tempId}
                      className="teamLeagueTeamsAuthPage-reviewCard"
                    >
                      <div className="teamLeagueTeamsAuthPage-reviewCardHeader">
                        <div>
                          <p className="teamLeagueTeamsAuthPage-reviewLabel">
                            Entered Team
                          </p>

                          <h3>{item.enteredName}</h3>
                        </div>

                        {item.matches.length > 0 ? (
                          <span className="teamLeagueTeamsAuthPage-matchBadge">
                            Possible match
                          </span>
                        ) : (
                          <span className="teamLeagueTeamsAuthPage-newBadge">
                            No match found
                          </span>
                        )}
                      </div>

                      {item.matches.length > 0 && (
                        <div className="teamLeagueTeamsAuthPage-matchList">
                          <p className="teamLeagueTeamsAuthPage-matchTitle">
                            Existing team records
                          </p>

                          {item.matches.map((match) => {
                            const isSelected =
                              item.action === "existing" &&
                              Number(item.selectedTeamId) === Number(match.id);

                            const historyLines = getTeamHistoryLines(match);

                            return (
                              <button
                                key={match.id}
                                type="button"
                                className={
                                  isSelected
                                    ? "teamLeagueTeamsAuthPage-matchCard teamLeagueTeamsAuthPage-selected"
                                    : "teamLeagueTeamsAuthPage-matchCard"
                                }
                                onClick={() =>
                                  updateTeamReviewChoice(item.tempId, {
                                    action: "existing",
                                    selectedTeamId: match.id,
                                    copyRoster: "",
                                  })
                                }
                              >
                                <div className="teamLeagueTeamsAuthPage-matchMainRow">
                                  <strong>{match.name}</strong>

                                  <span>
                                    {isSelected ? "Selected" : "Use this team"}
                                  </span>
                                </div>

                                <div className="teamLeagueTeamsAuthPage-historyList">
                                  {historyLines.slice(0, 3).map((historyItem, index) => (
                                    <small key={index}>{historyItem}</small>
                                  ))}
                                </div>

                                {match.rosterPreview?.length > 0 && (
                                  <div className="teamLeagueTeamsAuthPage-rosterPreviewList">
                                    <p className="teamLeagueTeamsAuthPage-rosterPreviewTitle">
                                      Previous roster
                                    </p>

                                    {match.rosterPreview.slice(0, 8).map((player) => (
                                      <small key={player.id}>
                                        {player.name}
                                      </small>
                                    ))}

                                    {match.rosterPreview.length > 8 && (
                                      <small className="teamLeagueTeamsAuthPage-rosterPreviewMore">
                                        +{match.rosterPreview.length - 8} more players
                                      </small>
                                    )}
                                  </div>
                                )}

                                {isSelected && (
                                  <div
                                    className="teamLeagueTeamsAuthPage-rosterSetupInline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="teamLeagueTeamsAuthPage-rosterSetupDivider" />

                                    <p className="teamLeagueTeamsAuthPage-rosterSetupTitle">
                                      Roster setup
                                    </p>

                                    <p className="teamLeagueTeamsAuthPage-rosterSetupSubtitle">
                                      Choose how this existing team should start in this league.
                                    </p>

                                    <div className="teamLeagueTeamsAuthPage-rosterSetupActions">
                                      <button
                                        type="button"
                                        className={
                                          item.copyRoster === "true"
                                            ? "teamLeagueTeamsAuthPage-rosterSetupBtn teamLeagueTeamsAuthPage-rosterSetupBtnSelected"
                                            : "teamLeagueTeamsAuthPage-rosterSetupBtn"
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateTeamReviewChoice(item.tempId, {
                                            copyRoster: "true",
                                          });
                                        }}
                                      >
                                        Copy Previous Roster
                                      </button>

                                      <button
                                        type="button"
                                        className={
                                          item.copyRoster === "false"
                                            ? "teamLeagueTeamsAuthPage-rosterSetupBtn teamLeagueTeamsAuthPage-rosterSetupBtnSelected"
                                            : "teamLeagueTeamsAuthPage-rosterSetupBtn"
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateTeamReviewChoice(item.tempId, {
                                            copyRoster: "false",
                                          });
                                        }}
                                      >
                                        Start Empty
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <button
                        type="button"
                        className={
                          item.action === "new"
                            ? "teamLeagueTeamsAuthPage-createNewCard teamLeagueTeamsAuthPage-selected"
                            : "teamLeagueTeamsAuthPage-createNewCard"
                        }
                        onClick={() =>
                          updateTeamReviewChoice(item.tempId, {
                            action: "new",
                            selectedTeamId: "",
                            copyRoster: "false",
                          })
                        }
                      >
                        <div>
                          <strong>Create New Team - </strong>

                          <small>
                            This creates a fresh team identity and adds it to this league.
                          </small>
                        </div>

                        <span>
                          {item.action === "new" ? "Selected" : "Choose"}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>

                <div className="teamLeagueTeamsAuthPage-modalActions">
                  <button
                    type="button"
                    className="teamLeagueTeamsAuthPage-modalCancelBtn"
                    onClick={() => {
                      setAddTeamStep("names");
                      setAddTeamError("");
                    }}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    className="teamLeagueTeamsAuthPage-modalSubmitBtn"
                    disabled={isSavingTeams}
                    onClick={handleAddTeamsToLeague}
                  >
                    {isSavingTeams ? "Adding..." : "Add Teams to League"}
                  </button>

                  <button
                    type="button"
                    className="teamLeagueTeamsAuthPage-modalCancelBtn"
                    onClick={() => {
                      setIsAddTeamsModalOpen(false);
                      resetAddTeamsModal();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="teamLeagueTeamsAuthPage-tableWrap">
        <table className="teamLeagueTeamsAuthPage-teamTable">
          <thead>
            <tr>
              <th className="teamLeagueTeamsAuthPage-checkboxCol">
                <input
                  type="checkbox"
                  checked={
                    teams.length > 0 && selectedTeams.length === teams.length
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th>Standings</th>
              <th>Teams</th>
              <th>Captain</th>
              <th>Players</th>
              <th>Games</th>
              <th>Wins</th>
              <th>Losses</th>
              {showTeamHideControls && <th>Hide</th>}
              <th className="teamLeagueTeamsAuthPage-actionCol"></th>
            </tr>
          </thead>

          <tbody>
            {sortedTeams.length === 0 ? (
              <tr>
                <td
                  colSpan={showTeamHideControls ? "10" : "9"}
                  className="teamLeagueTeamsAuthPage-emptyCell"
                >
                  No teams available
                </td>
              </tr>
            ) : (
              sortedTeams.map((team) => (
                <tr key={team.id}>
                  <td className="teamLeagueTeamsAuthPage-checkboxCol">
                    <input
                      type="checkbox"
                      checked={selectedTeams.includes(team.id)}
                      onChange={() => handleCheckboxChange(team.id)}
                    />
                  </td>

                  <td>{team.rank || "-"}</td>

                  <td className="teamLeagueTeamsAuthPage-teamNameCell">
                    {team.playersCount < 5 && (
                      <span className="teamLeagueTeamsAuthPage-warningTooltip">
                        ⚠️
                        <span className="teamLeagueTeamsAuthPage-tooltipText">
                          Team has fewer than 5 players
                        </span>
                      </span>
                    )}

                    <NavLink
                      to={`/dashboard/seasons/${seasonId}/leagues/${leagueId}/teams/${team.id}`}
                    >
                      {team.name}
                    </NavLink>
                  </td>

                  <td>{team.captain || "-"}</td>
                  <td>{team.playersCount || 0}</td>

                  <td>
                    {teamGameCounts[
                      Number(team.seasons_leagues_teams_id || team.id)
                    ] || 0}
                  </td>

                  <td>{team.wins ?? 0}</td>
                  <td>{team.losses ?? 0}</td>

                  {showTeamHideControls && (
                    <td>
                      <input
                        type="checkbox"
                        checked={!!team.hidden}
                        onChange={() => handleToggleHide(team.id)}
                      />
                    </td>
                  )}

                  <td className="teamLeagueTeamsAuthPage-actionCol">
                    <button
                      type="button"
                      className="teamLeagueTeamsAuthPage-pencilBtn"
                      onClick={() => openUpdateModal(team)}
                    >
                      ✏️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isUpdateModalOpen && (
        <div className="teamLeagueTeamsAuthPage-editModalOverlay">
          <div className="teamLeagueTeamsAuthPage-editModal">
            <div className="teamLeagueTeamsAuthPage-editModalHeader">
              <h3>Edit Team</h3>

              <button
                type="button"
                className="teamLeagueTeamsAuthPage-editCloseBtn"
                onClick={() => setIsUpdateModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form
              className="teamLeagueTeamsAuthPage-editForm"
              onSubmit={handleUpdateTeam}
            >
              <div className="teamLeagueTeamsAuthPage-editField">
                <label>Team Name</label>

                <input
                  type="text"
                  value={updateForm.name}
                  onChange={(e) =>
                    setUpdateForm({ ...updateForm, name: e.target.value })
                  }
                />
              </div>

              <div className="teamLeagueTeamsAuthPage-editImageGrid">
                <div className="teamLeagueTeamsAuthPage-editImageCard">
                  <label>Default Logo</label>

                  {updateForm.existingLogo && (
                    <img
                      src={updateForm.existingLogo}
                      alt="Current team logo"
                      className="teamLeagueTeamsAuthPage-editLogoPreview teamLeagueTeamsAuthPage-editClickableImage"
                      onClick={() => setPreviewImage(updateForm.existingLogo)}
                    />
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </div>

                <div className="teamLeagueTeamsAuthPage-editImageCard">
                  <label>Season Logo</label>

                  {updateForm.existingSeasonLogo && (
                    <img
                      src={updateForm.existingSeasonLogo}
                      alt="Current season team logo"
                      className="teamLeagueTeamsAuthPage-editLogoPreview teamLeagueTeamsAuthPage-editClickableImage"
                      onClick={() =>
                        setPreviewImage(updateForm.existingSeasonLogo)
                      }
                    />
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSeasonLogoChange}
                  />
                </div>

                <div className="teamLeagueTeamsAuthPage-editImageCard">
                  <label>Season Team Photo</label>

                  {updateForm.existingSeasonTeamPhoto && (
                    <img
                      src={updateForm.existingSeasonTeamPhoto}
                      alt="Current season team photo"
                      className="teamLeagueTeamsAuthPage-editPhotoPreview teamLeagueTeamsAuthPage-editClickableImage"
                      onClick={() =>
                        setPreviewImage(updateForm.existingSeasonTeamPhoto)
                      }
                    />
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSeasonTeamPhotoChange}
                  />
                </div>
              </div>

              <div className="teamLeagueTeamsAuthPage-editField">
                <label>Select Existing Player Captain</label>

                <input
                  type="text"
                  placeholder="Search player..."
                  value={captainSearch}
                  onChange={(e) => {
                    setCaptainSearch(e.target.value);
                    setUpdateForm((prev) => ({
                      ...prev,
                      captainId: "",
                      captain: "",
                    }));
                  }}
                />

                {captainSearch && (
                  <div className="teamLeagueTeamsAuthPage-editSearchDropdown">
                    {allPlayers
                      .filter((player) =>
                        `${player.firstName} ${player.lastName}`
                          .toLowerCase()
                          .includes(captainSearch.toLowerCase())
                      )
                      .slice(0, 10)
                      .map((player) => {
                        const teamNames = [
                          ...new Set(
                            player.rosters
                              ?.map((roster) => roster.team?.name)
                              .filter(Boolean)
                          ),
                        ].join(", ");

                        return (
                          <div
                            key={player.id}
                            className="teamLeagueTeamsAuthPage-editSearchItem"
                            onClick={() => {
                              setUpdateForm((prev) => ({
                                ...prev,
                                captainId: player.id,
                                captain: `${player.firstName} ${player.lastName}`,
                              }));

                              setCaptainSearch(
                                `${player.firstName} ${player.lastName}`
                              );
                            }}
                          >
                            {player.firstName} {player.lastName}
                            {teamNames ? ` (${teamNames})` : ""}
                          </div>
                        );
                      })}
                  </div>
                )}

                <select
                  value={updateForm.captainId}
                  onChange={(e) => {
                    const selected = allPlayers.find(
                      (player) => player.id === parseInt(e.target.value)
                    );

                    setUpdateForm({
                      ...updateForm,
                      captainId: selected?.id || "",
                      captain: selected
                        ? `${selected.firstName} ${selected.lastName}`
                        : "",
                    });

                    setCaptainSearch("");
                  }}
                >
                  <option value="">-- Or Select from List --</option>

                  {[...allPlayers]
                    .sort((a, b) => a.firstName.localeCompare(b.firstName))
                    .map((player) => {
                      const teamNames = [
                        ...new Set(
                          player.rosters
                            ?.map((roster) => roster.team?.name)
                            .filter(Boolean)
                        ),
                      ].join(", ");

                      return (
                        <option key={player.id} value={player.id}>
                          {player.firstName} {player.lastName}
                          {teamNames ? ` (${teamNames})` : ""}
                        </option>
                      );
                    })}
                </select>
              </div>

              <div className="teamLeagueTeamsAuthPage-editActions">
                <button
                  type="button"
                  onClick={() => setIsUpdateModalOpen(false)}
                >
                  Cancel
                </button>

                <button type="submit">Update Team</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeagueTeamsAuth;